import { useEffect } from 'react';

/**
 * Hook pour gérer les raccourcis clavier
 */
export function useKeyboardShortcuts({
  dancers,
  selectedDancerId,
  setSelectedDancerId,
  setSelectedPositionId,
  periods,
  currentPeriodIndex,
  setCurrentPeriodIndex,
  audioRef,
  setCurrentTime,
  setIsPlaying,
  // Paramètres pour le mode link
  cursorMode,
  linkChain,
  handleCreateLinkGroup,
  setCursorMode,
  // Paramètres pour les menus contextuels
  contextMenuPosition,
  setContextMenuPosition,
  dancerContextMenu,
  setDancerContextMenu,
  // Paramètres pour le menu d'erreurs
  showErrorMenu,
  setShowErrorMenu,
}) {
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Vérifier si l'utilisateur est en train de taper dans un champ texte
      const activeElement = document.activeElement;
      if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA' || activeElement.isContentEditable)) {
        return;
      }

      // ✅ Touche Enter en mode link : valider le groupe de liaisons
      if (e.key === 'Enter' && cursorMode === 'link' && linkChain && linkChain.length >= 2) {
        e.preventDefault();
        if (handleCreateLinkGroup) {
          handleCreateLinkGroup();
        }
        return;
      }

      // ✅ Touche Échap : fermer dans l'ordre - clique droit, erreur, curseur normal
      if (e.key === 'Escape') {
        e.preventDefault();
        
        // 1. Vérifier si un menu contextuel (clic droit) est ouvert
        const isContextMenuOpen = contextMenuPosition?.visible || dancerContextMenu?.visible;
        
        if (isContextMenuOpen) {
          // Fermer les menus contextuels en priorité
          if (contextMenuPosition?.visible && setContextMenuPosition) {
            setContextMenuPosition({ ...contextMenuPosition, visible: false });
          }
          if (dancerContextMenu?.visible && setDancerContextMenu) {
            setDancerContextMenu({ ...dancerContextMenu, visible: false });
          }
          return;
        }
        
        // 2. Si pas de menu contextuel, vérifier si le menu d'erreurs est ouvert
        if (showErrorMenu && setShowErrorMenu) {
          setShowErrorMenu(false);
          return;
        }
        
        // 3. Si ni menu contextuel ni menu d'erreurs, vérifier le curseur
        if (cursorMode && cursorMode !== 'default' && setCursorMode) {
          setCursorMode('default');
          return;
        }
        
        // Sinon, ne rien faire (tout est déjà fermé/par défaut)
        return;
      }

      if (e.key === 'Tab') {
        e.preventDefault();

        if (e.shiftKey) {
          // Shift+Tab → Revient au danseur précédent
          if (dancers.length > 0) {
            setSelectedDancerId((prevId) => {
              const currentIndex = dancers.findIndex(d => d.id === prevId);
              const newIndex = (currentIndex - 1 + dancers.length) % dancers.length;
              const newDancer = dancers[newIndex];

              // Mettre à jour la position
              const period = periods[currentPeriodIndex];
              const dancerPosition = period?.positions.find(pos => pos.dancer_id === newDancer.id);
              setSelectedPositionId(dancerPosition ? dancerPosition.id : null);

              // Forcer le focus sur le nouveau danseur
              setTimeout(() => {
                const dancerElement = document.getElementById(`dancer-${newDancer.id}`);
                if (dancerElement) dancerElement.focus();
              }, 10);

              return newDancer.id;
            });
          }
        } else {
          // Tab → Passe au danseur suivant
          if (dancers.length > 0) {
            setSelectedDancerId((prevId) => {
              const currentIndex = dancers.findIndex(d => d.id === prevId);
              const newIndex = (currentIndex + 1) % dancers.length;
              const newDancer = dancers[newIndex];

              // Mettre à jour la position
              const period = periods[currentPeriodIndex];
              const dancerPosition = period?.positions.find(pos => pos.dancer_id === newDancer.id);
              setSelectedPositionId(dancerPosition ? dancerPosition.id : null);

              // Forcer le focus sur le nouveau danseur
              setTimeout(() => {
                const dancerElement = document.getElementById(`dancer-${newDancer.id}`);
                if (dancerElement) dancerElement.focus();
              }, 10);

              return newDancer.id;
            });
          }
        }
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        // Flèche droite → Passe à la période suivante (dans l'ordre des timestamps)
        if (periods.length > 0) {
          // Arrêter la lecture
          setIsPlaying(false);
          
          // Trier les périodes par start_time
          const sortedPeriods = [...periods].sort((a, b) => (a.start_time ?? Infinity) - (b.start_time ?? Infinity));
          const currentPeriod = periods[currentPeriodIndex];
          const currentSortedIndex = sortedPeriods.findIndex(p => p.id === currentPeriod?.id);
          const nextSortedIndex = (currentSortedIndex + 1) % sortedPeriods.length;
          const nextPeriod = sortedPeriods[nextSortedIndex];
          const newIndex = periods.findIndex(p => p.id === nextPeriod?.id);
          
          setCurrentPeriodIndex(newIndex);
          
          // Forcer le focus sur la nouvelle période
          setTimeout(() => {
            const periodElement = document.getElementById(`period-${nextPeriod?.id}`);
            if (periodElement) periodElement.focus();
          }, 10);
          
          // Mettre à jour la barre de progression audio
          if (audioRef.current && nextPeriod) {
            audioRef.current.currentTime = nextPeriod.start_time;
            setCurrentTime(nextPeriod.start_time);
          }
        }
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        // Flèche gauche → Revient à la période précédente (dans l'ordre des timestamps)
        if (periods.length > 0) {
          // Arrêter la lecture
          setIsPlaying(false);
          
          // Trier les périodes par start_time
          const sortedPeriods = [...periods].sort((a, b) => (a.start_time ?? Infinity) - (b.start_time ?? Infinity));
          const currentPeriod = periods[currentPeriodIndex];
          const currentSortedIndex = sortedPeriods.findIndex(p => p.id === currentPeriod?.id);
          const prevSortedIndex = (currentSortedIndex - 1 + sortedPeriods.length) % sortedPeriods.length;
          const prevPeriod = sortedPeriods[prevSortedIndex];
          const newIndex = periods.findIndex(p => p.id === prevPeriod?.id);
          
          setCurrentPeriodIndex(newIndex);
          
          // Forcer le focus sur la nouvelle période
          setTimeout(() => {
            const periodElement = document.getElementById(`period-${prevPeriod?.id}`);
            if (periodElement) periodElement.focus();
          }, 10);
          
          // Mettre à jour la barre de progression audio
          if (audioRef.current && prevPeriod) {
            audioRef.current.currentTime = prevPeriod.start_time;
            setCurrentTime(prevPeriod.start_time);
          }
        }
      } else if (e.key === ' ') {
        e.preventDefault();
        setIsPlaying(prev => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    dancers, 
    selectedDancerId, 
    periods, 
    currentPeriodIndex, 
    audioRef, 
    setSelectedDancerId, 
    setSelectedPositionId, 
    setCurrentPeriodIndex, 
    setCurrentTime, 
    setIsPlaying,
    cursorMode,
    linkChain,
    handleCreateLinkGroup,
    setCursorMode,
    contextMenuPosition,
    setContextMenuPosition,
    dancerContextMenu,
    setDancerContextMenu,
  ]);
}
